// Pure sort / group / filter helpers for the Everyday › Habits tab, shared by web and native.

export type HabitSortKey = 'default' | 'streak' | 'strength' | 'time' | 'title'
export type HabitGroupKey = 'none' | 'time' | 'category' | 'cadence' | 'tag'
export type HabitDueKey = 'due' | 'done' | 'notDue'

export interface HabitFilter {
  sort: HabitSortKey
  group: HabitGroupKey
  due: HabitDueKey[]
  categories: string[]
  cadences: string[]
  tags: string[]
}

export const defaultHabitFilter: HabitFilter = {
  sort: 'default',
  group: 'time',
  due: [],
  categories: [],
  cadences: [],
  tags: [],
}

export const HABIT_DEFAULT_GROUP: HabitGroupKey = 'time'

export type TimeBucket = 'morning' | 'afternoon' | 'evening' | 'anytime'
export const TIME_BUCKETS: readonly TimeBucket[] = ['morning', 'afternoon', 'evening', 'anytime']

/** Morning < 12:00, afternoon 12:00-16:59, evening 17:00+, anytime when no (valid) time is set. */
export function timeBucket(time: string | null | undefined): TimeBucket {
  const m = /^(\d{1,2}):(\d{2})$/.exec(time ?? '')
  if (!m) return 'anytime'
  const h = Number(m[1])
  if (h > 23) return 'anytime'
  return h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening'
}

// Words in a title that name a part of the day. They win over the stored time, which is
// often just the 09:00 default ("Pray every night" @ 09:00 belongs in the evening).
const TITLE_HINTS: [RegExp, TimeBucket][] = [
  [
    /\b(night|evening|bed ?time|before bed|sleep|sunset|dinner)\b|夜|晩|寝る前|晚上|睡前|noche|noite|soir/i,
    'evening',
  ],
  [/\b(morning|wake|waking|sunrise|breakfast)\b|朝|早上|早晨|mañana|manhã|matin/i, 'morning'],
  [/\b(afternoon|lunch|midday|noon)\b|昼|午後|下午|午饭|tarde|après-midi/i, 'afternoon'],
]

export function habitTimeBucket(h: { title: string; recurrenceTime?: string | null }): TimeBucket {
  const hint = TITLE_HINTS.find(([re]) => re.test(h.title))
  return hint ? hint[1] : timeBucket(h.recurrenceTime)
}

export type Cadence =
  | 'daily'
  | 'weekday'
  | 'weekend'
  | 'days'
  | 'everyOtherDay'
  | 'weekly'
  | 'monthly'
export const CADENCES: readonly Cadence[] = [
  'daily',
  'weekday',
  'weekend',
  'days',
  'everyOtherDay',
  'weekly',
  'monthly',
]

const sameDays = (days: readonly number[], want: readonly number[]) =>
  days.length === want.length && want.every((d) => days.includes(d))

/** Canonical cadence of a habit; a "specific days" set that spells out another cadence collapses to it. */
export function cadenceOf(
  period: string | null | undefined,
  days?: readonly number[] | null,
): Cadence {
  switch (period) {
    case 'everyOtherDay':
    case 'weekly':
    case 'monthly':
    case 'weekday':
    case 'weekend':
      return period
    case 'days': {
      const set = [...new Set(days ?? [])]
      if (set.length === 0 || set.length === 7) return 'daily'
      if (sameDays(set, [1, 2, 3, 4, 5])) return 'weekday'
      if (sameDays(set, [0, 6])) return 'weekend'
      return 'days'
    }
    default:
      return 'daily'
  }
}

/** i18n key (under the existing `inbox.period.*`) naming a cadence. */
export const cadenceLabelKey = (c: Cadence) => `inbox.period.${c}`

const norm = (s: string | null | undefined) => (s && s !== 'inbox' ? s : '')

export type HabitView = {
  title: string
  tags: string[]
  category: string | null | undefined
  cadence: Cadence
  recurrenceTime: string | null | undefined
  streak: number
  strength: number
  scheduledToday: boolean
  doneToday: boolean
  order: number
}

export function matchesDue(due: readonly HabitDueKey[], h: HabitView): boolean {
  if (due.length === 0) return true
  if (h.doneToday) return due.includes('done')
  return due.includes(h.scheduledToday ? 'due' : 'notDue')
}

export function filterHabits<T>(
  items: T[],
  f: Pick<HabitFilter, 'due' | 'categories' | 'cadences' | 'tags'>,
  view: (x: T) => HabitView,
): T[] {
  return items.filter((x) => {
    const h = view(x)
    if (!matchesDue(f.due, h)) return false
    if (f.categories.length && !f.categories.includes(norm(h.category))) return false
    if (f.cadences.length && !f.cadences.includes(h.cadence)) return false
    if (f.tags.length && !h.tags.some((t) => f.tags.includes(t))) return false
    return true
  })
}

const timeKey = (h: HabitView) => h.recurrenceTime || '99:99'

/** Sorts a copy. Default keeps the caller's order, except time-of-day grouping which orders by time. */
export function sortHabits<T>(
  items: T[],
  sort: HabitSortKey,
  group: HabitGroupKey,
  view: (x: T) => HabitView,
): T[] {
  const s = [...items]
  const eff = sort === 'default' && group === 'time' ? 'time' : sort
  switch (eff) {
    case 'streak':
      return s.sort((a, b) => view(b).streak - view(a).streak)
    case 'strength':
      return s.sort((a, b) => view(b).strength - view(a).strength)
    case 'time':
      return s.sort((a, b) => timeKey(view(a)).localeCompare(timeKey(view(b))))
    case 'title':
      return s.sort((a, b) =>
        view(a).title.localeCompare(view(b).title, undefined, { sensitivity: 'base' }),
      )
    default:
      return s
  }
}

export type HabitSection<T> = { key: string; items: T[] }

/** Groups in a stable header order; empty sections are dropped. Header text is resolved by the caller from `key`. */
export function groupHabits<T>(
  items: T[],
  group: HabitGroupKey,
  view: (x: T) => HabitView,
  allTags: readonly string[],
  categoryOrder: readonly string[],
): HabitSection<T>[] {
  if (group === 'none') return [{ key: 'all', items }]
  const by = (keys: readonly string[], pick: (h: HabitView) => string) =>
    keys
      .map((key) => ({ key, items: items.filter((x) => pick(view(x)) === key) }))
      .filter((s) => s.items.length > 0)
  switch (group) {
    case 'time':
      return by(TIME_BUCKETS, habitTimeBucket)
    case 'cadence':
      return by(CADENCES, (h) => h.cadence)
    case 'category':
      return by([...categoryOrder, ''], (h) => norm(h.category))
    case 'tag': {
      const out = allTags
        .map((key) => ({ key, items: items.filter((x) => view(x).tags.includes(key)) }))
        .filter((s) => s.items.length > 0)
      const none = items.filter((x) => view(x).tags.length === 0)
      return none.length ? [...out, { key: '', items: none }] : out
    }
  }
}
